import React, { useEffect, useState } from 'react';
import type { FacetIconProps } from './facet-icon';

/**
 * Lazy front for facet-icon.tsx, for the SearchPanel. That module's icon tables (about 100 Phosphor
 * icons plus the simple-icons brand marks, ~290 KB before minification) were in every page's
 * bundle because the navbar and mobile bottom nav import the panel, which only renders once opened.
 * The panel is never server-rendered, so it can show an empty same-size slot for the moment before
 * the module arrives. Launchers call preloadFacetIcons() on hover/focus/touch so it's usually
 * there before the panel opens.
 *
 * Server-rendered callers (the job page's EditorialDossier) keep importing facet-icon.tsx directly.
 */
type FacetIconModule = typeof import('./facet-icon');

let loaded: FacetIconModule | null = null;
let pending: Promise<FacetIconModule> | null = null;

export function preloadFacetIcons(): Promise<FacetIconModule> {
  if (loaded) return Promise.resolve(loaded);
  pending ??= import('./facet-icon').then(
    (mod) => (loaded = mod),
    (err) => {
      pending = null;
      throw err;
    },
  );
  return pending;
}

function useFacetIconModule(): FacetIconModule | null {
  const [mod, setMod] = useState<FacetIconModule | null>(loaded);
  useEffect(() => {
    if (mod) return;
    let live = true;
    preloadFacetIcons().then(
      (m) => {
        if (live) setMod(m);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [mod]);
  return mod;
}

function IconSlot({ size, className }: { size: number; className?: string }) {
  return <span aria-hidden="true" className={className} style={{ display: 'inline-block', width: size, height: size, flexShrink: 0 }} />;
}

export function FacetIcon(props: FacetIconProps) {
  const mod = useFacetIconModule();
  if (!mod) return <IconSlot size={props.size ?? 24} className={props.className} />;
  return <mod.FacetIcon {...props} />;
}

export function TopHitCompanyLogo(props: Parameters<FacetIconModule['TopHitCompanyLogo']>[0]) {
  const mod = useFacetIconModule();
  if (!mod) return <IconSlot size={props.size} className={props.className} />;
  return <mod.TopHitCompanyLogo {...props} />;
}
